import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../lib/lang-context";
import { LOGO_URL } from "../lib/i18n";
import { List, X, UserCircle } from "@phosphor-icons/react";
import ThemeToggle from "./ThemeToggle";
import PromoBar from "./PromoBar";

const navItems = [
  { id: "home", href: "#home" },
  { id: "products", href: "#products" },
  { id: "process", href: "#process" },
  { id: "sectors", href: "#sectors" },
  { id: "coverage", href: "#coverage" },
  { id: "blog", href: "#blog" },
  { id: "faq", href: "#faq" },
  { id: "contact", href: "#contact" },
];

export default function Header() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const buildHref = (h) => (onHome ? h : `/${h}`);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-white/10" : "border-b border-transparent"
      }`}
    >
      <PromoBar />
      <div className="bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-28 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="header-logo"
        >
          <img
            src={LOGO_URL}
            alt="ICOR"
            className="h-20 md:h-24 w-auto"
            style={{
              mixBlendMode: "screen",
              filter: "brightness(2.8) contrast(1.1)",
            }}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={buildHref(item.href)}
              data-testid={`nav-link-${item.id}`}
              className="text-sm tracking-tight text-white/70 hover:text-white transition-colors duration-200"
            >
              {t.nav[item.id]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/portal/login"
            data-testid="header-portal-link"
            className="hidden md:inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white border border-white/20 hover:border-white/50 rounded-sm px-3 py-2 transition-all"
          >
            <UserCircle size={16} weight="bold" />
            <span className="hidden lg:inline">{t.nav.portal}</span>
          </Link>

          <ThemeToggle />

          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-1 text-xs font-semibold tracking-[0.18em] text-white/80 hover:text-white border border-white/20 hover:border-white/50 rounded-sm px-3 py-2 transition-all"
            aria-label="Toggle language"
          >
            <span className={lang === "es" ? "text-white" : "text-white/40"}>
              ES
            </span>
            <span className="text-white/30">/</span>
            <span className={lang === "en" ? "text-white" : "text-white/40"}>
              EN
            </span>
          </button>

          <a
            href={buildHref("#quote")}
            data-testid="header-cta"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white transition-all duration-300"
            style={{ background: "var(--icor-blue)", borderRadius: 4 }}
          >
            {t.nav.cta}
          </a>

          <button
            className="xl:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
          >
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-black border-t border-white/10">
          <nav className="px-6 py-4 flex flex-col gap-4">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={buildHref(item.href)}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.id}`}
                className="text-base text-white/80 hover:text-white"
              >
                {t.nav[item.id]}
              </a>
            ))}
            <Link
              to="/portal/login"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-portal"
              className="text-base text-white/80 hover:text-white"
            >
              {t.nav.portal}
            </Link>
            <button
              onClick={toggle}
              data-testid="mobile-lang-toggle"
              className="text-left text-sm font-semibold tracking-[0.18em] text-white/70"
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
          </nav>
        </div>
      )}
      </div>
    </header>
  );
}
